import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, DollarSign, TrendingUp, AlertCircle, CheckCircle2, Clock, Check, Undo2, Sparkles, CreditCard, Copy, Star } from 'lucide-react';

const API = window.location.origin + '/api';

const STATUS_META = {
  active:   { label: 'Paid this month', bg: '#D1FAE5', color: '#065F46', icon: CheckCircle2 },
  trialing: { label: 'In trial',        bg: '#DBEAFE', color: '#1E3A8A', icon: Sparkles },
  overdue:  { label: 'Overdue',         bg: '#FEE2E2', color: '#991B1B', icon: AlertCircle },
  no_admin: { label: 'No admin yet',    bg: '#F3F4F6', color: '#475569', icon: Clock },
  paused:   { label: 'Paused',          bg: '#FEF3C7', color: '#92400E', icon: Clock },
};

const nfmt = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ icon: Icon, label, value, sublabel, color = '#0a1f44', bg = '#f8fafc' }) {
  return (
    <div style={{
      background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12,
      padding: '16px 20px', display: 'flex', gap: 14, alignItems: 'center'
    }}>
      <div style={{ background: bg, color, width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} />
      </div>
      <div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 0.03, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--navy)', lineHeight: 1.15 }}>{value}</div>
        {sublabel && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

function trialLabel(row) {
  if (!row.has_billing) return '—';
  if (row.days_until_trial_end === null || row.days_until_trial_end === undefined) return '—';
  const d = row.days_until_trial_end;
  if (d > 0) return `${d} days left`;
  if (d === 0) return 'ends today';
  return `ended ${Math.abs(d)}d ago`;
}

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [impressions, setImpressions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [linkModal, setLinkModal] = useState(null); // { store, url }
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/billing`, { withCredentials: true }),
      axios.get(`${API}/admin/promos?`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/promos/impressions?days=7`, { withCredentials: true }).catch(() => ({ data: { totals: {} } })),
    ])
      .then(([billing, promos, impr]) => {
        setData(billing.data);
        // Aggregate premium impressions + clicks per supermarket
        const totals = impr.data?.totals || {};
        const promosList = Array.isArray(promos.data) ? promos.data : [];
        const byStore = {};
        for (const p of promosList) {
          if (p.slot_type === 'premium' && p.active && p.supermarket_id) {
            const t = totals[p.id] || { impressions: 0, unique_viewers: 0, clicks: 0, unique_clickers: 0 };
            const cur = byStore[p.supermarket_id] || { impressions: 0, unique_viewers: 0, clicks: 0, unique_clickers: 0 };
            cur.impressions += t.impressions || 0;
            cur.unique_viewers += t.unique_viewers || 0;
            cur.clicks += t.clicks || 0;
            cur.unique_clickers += t.unique_clickers || 0;
            byStore[p.supermarket_id] = cur;
          }
        }
        // Derive per-store CTR
        for (const sid in byStore) {
          const s = byStore[sid];
          s.ctr_percent = s.impressions > 0 ? Math.round((s.clicks / s.impressions) * 1000) / 10 : 0;
        }
        setImpressions(byStore);
      })
      .catch(err => setError(err.response?.data?.detail || 'Could not load billing'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (row) => {
    setBusy(row.supermarket_id);
    try {
      await axios.post(`${API}/admin/supermarkets/${row.supermarket_id}/billing/payments`,
        { month: row.current_month, amount: row.monthly_price },
        { withCredentials: true });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not mark paid');
    } finally {
      setBusy('');
    }
  };

  const unmarkPaid = async (row) => {
    if (!window.confirm(`Undo payment for ${row.name} (${row.current_month})?`)) return;
    setBusy(row.supermarket_id);
    try {
      await axios.delete(`${API}/admin/supermarkets/${row.supermarket_id}/billing/payments/${row.current_month}`,
        { withCredentials: true });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not undo payment');
    } finally {
      setBusy('');
    }
  };

  const sendPaymentLink = async (row) => {
    setBusy(row.supermarket_id);
    try {
      const { data } = await axios.post(`${API}/payments/initiate/subscription`,
        { supermarket_id: row.supermarket_id, month: row.current_month, amount: row.monthly_price },
        { withCredentials: true });
      const url = data.checkout_url?.startsWith('http') ? data.checkout_url : window.location.origin + data.checkout_url;
      setLinkModal({ store: row, url });
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not generate payment link');
    } finally {
      setBusy('');
    }
  };

  const copyLink = async () => {
    if (!linkModal?.url) return;
    try {
      await navigator.clipboard.writeText(linkModal.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="admin-page" data-testid="admin-billing-page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <h2 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <DollarSign size={22} /> SaaS Billing
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px' }}>
        Track K150/month per supermarket. Mark payments as you receive them (mobile money, cash, or bank transfer).
      </p>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
      {error && <div style={{ padding: 12, background: '#FEF2F2', color: '#991B1B', borderRadius: 8 }}>{error}</div>}

      {data && (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 22 }}>
            <StatCard icon={CheckCircle2} label={`Paid — ${data.current_month}`} value={data.totals.current_month_paid_stores} sublabel="supermarkets" color="#065F46" bg="#D1FAE5" />
            <StatCard icon={Sparkles}    label="In free trial"  value={data.totals.trialing}  sublabel="supermarkets" color="#1E3A8A" bg="#DBEAFE" />
            <StatCard icon={AlertCircle} label="Overdue"        value={data.totals.overdue}   sublabel="need follow-up" color="#991B1B" bg="#FEE2E2" />
            <StatCard icon={TrendingUp}  label="YTD collected"  value={`K${nfmt.format(data.totals.ytd_paid)}`} sublabel="this year" color="#7C3AED" bg="#EDE9FE" />
            <StatCard icon={Star}
              label="Premium ad add-on"
              value={`K${nfmt.format(data.totals.premium_add_on_monthly || 0)}`}
              sublabel={`${data.totals.premium_slots_active || 0} active${data.totals.premium_slots_requested ? ` · ${data.totals.premium_slots_requested} requested` : ''}`}
              color="#92400E" bg="#FEF3C7" />
          </div>

          {/* Table */}
          <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left', color: 'var(--text-muted)' }}>
                    <th style={cellHead}>Supermarket</th>
                    <th style={cellHead}>Status</th>
                    <th style={cellHead}>Trial</th>
                    <th style={cellHead}>Monthly</th>
                    <th style={cellHead}>Premium slots</th>
                    <th style={cellHead}>YTD</th>
                    <th style={{ ...cellHead, textAlign: 'right' }}>{data.current_month}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stores.map(row => {
                    const meta = STATUS_META[row.status] || STATUS_META.no_admin;
                    const Icon = meta.icon;
                    return (
                      <tr key={row.supermarket_id} data-testid={`billing-row-${row.supermarket_id}`}
                        style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={cellBody}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{row.name}</div>
                          {row.months_paid_this_year > 0 && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                              {row.months_paid_this_year} month{row.months_paid_this_year === 1 ? '' : 's'} paid this year
                            </div>
                          )}
                        </td>
                        <td style={cellBody}>
                          <span data-testid={`billing-status-${row.supermarket_id}`}
                            style={{ background: meta.bg, color: meta.color, padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.05, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Icon size={11} /> {meta.label}
                          </span>
                        </td>
                        <td style={{ ...cellBody, color: '#475569' }}>{trialLabel(row)}</td>
                        <td style={{ ...cellBody, color: '#475569' }}>
                          {row.has_billing ? (
                            <>
                              <div>K{nfmt.format(row.monthly_price || 0)}<span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}> base</span></div>
                              {row.premium_add_on_monthly > 0 && (
                                <div style={{ fontSize: '0.72rem', color: '#92400E', marginTop: 2 }}>
                                  + K{nfmt.format(row.premium_add_on_monthly)} premium
                                </div>
                              )}
                              <div style={{ fontSize: '0.72rem', color: 'var(--navy)', marginTop: 2, fontWeight: 700 }}>
                                = K{nfmt.format(row.total_monthly || row.monthly_price || 0)}/mo
                              </div>
                            </>
                          ) : '—'}
                        </td>
                        <td style={cellBody}>
                          {row.has_billing ? (
                            <PremiumSlotCell row={row} impressions={impressions[row.supermarket_id]} onSaved={load} />
                          ) : '—'}
                        </td>
                        <td style={{ ...cellBody, fontWeight: 600, color: 'var(--navy)' }}>K{nfmt.format(row.ytd_paid)}</td>
                        <td style={{ ...cellBody, textAlign: 'right' }}>
                          {!row.has_billing && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>invite admin first</span>
                          )}
                          {row.has_billing && row.current_month_paid && (
                            <button onClick={() => unmarkPaid(row)} disabled={busy === row.supermarket_id}
                              data-testid={`unmark-paid-${row.supermarket_id}`}
                              style={{ padding: '6px 10px', background: '#F3F4F6', color: '#475569', border: 'none', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Undo2 size={12} /> Undo
                            </button>
                          )}
                          {row.has_billing && !row.current_month_paid && (
                            <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button onClick={() => sendPaymentLink(row)} disabled={busy === row.supermarket_id}
                                data-testid={`send-link-${row.supermarket_id}`}
                                style={{ padding: '7px 12px', background: '#0369A1', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <CreditCard size={12} /> Send link
                              </button>
                              <button onClick={() => markPaid(row)} disabled={busy === row.supermarket_id}
                                data-testid={`mark-paid-${row.supermarket_id}`}
                                style={{ padding: '7px 14px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Check size={13} /> Mark paid
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {data.stores.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No supermarkets yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ marginTop: 16, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            💡 Billing tracking is manual. When Phase C (automated Stripe/Razorpay) lands, this dashboard will show live subscription state instead of hand-marked payments.
          </p>

          {linkModal && (
            <div onClick={() => setLinkModal(null)} data-testid="link-modal"
              style={{ position: 'fixed', inset: 0, background: 'rgba(10,31,68,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div onClick={e => e.stopPropagation()}
                style={{ background: 'white', maxWidth: 480, width: '100%', borderRadius: 14, padding: 24 }}>
                <h3 style={{ margin: '0 0 4px', color: 'var(--navy)' }}>Payment link ready</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 14px' }}>
                  Share this link with <strong>{linkModal.store.name}</strong>. It opens the Cloudcode hosted checkout (BSP Pay, Visa/Mastercard, CellMoni, MiCash). Once paid, the {linkModal.store.current_month} row auto-flips to green.
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <code style={{ flex: 1, background: '#f8fafc', padding: '9px 12px', borderRadius: 6, fontSize: '0.78rem', border: '1px solid var(--border-gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid="payment-link-url">
                    {linkModal.url}
                  </code>
                  <button onClick={copyLink} data-testid="payment-link-copy"
                    style={{ padding: '9px 14px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Hi ${linkModal.store.name} 👋 Here's your StoreRuns subscription payment link for ${linkModal.store.current_month} (K${linkModal.store.monthly_price}):\n\n${linkModal.url}`)}`}
                   target="_blank" rel="noopener noreferrer" data-testid="payment-link-wa"
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#25D366', color: 'white', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none' }}>
                  Share on WhatsApp
                </a>
                <button onClick={() => setLinkModal(null)}
                  style={{ marginLeft: 8, padding: '9px 14px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-gray)', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const cellHead = { padding: '10px 14px', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.05 };
const cellBody = { padding: '12px 14px', verticalAlign: 'top' };

function PremiumSlotCell({ row, impressions, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(row.premium_slot_price ?? 100);
  const [saving, setSaving] = useState(false);
  const active = row.premium_slots_active || 0;
  const requested = row.premium_slots_requested || 0;
  const totalImpr = impressions?.impressions || 0;
  const uniqueImpr = impressions?.unique_viewers || 0;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/supermarkets/${row.supermarket_id}/billing`,
        { premium_slot_price: Number(price) || 0 },
        { withCredentials: true });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not update price');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`premium-slot-cell-${row.supermarket_id}`} style={{ fontSize: '0.78rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: active > 0 ? '#92400E' : 'var(--text-muted)', fontWeight: active > 0 ? 700 : 500 }}>
        <Star size={11} fill={active > 0 ? '#F59E0B' : 'none'} />
        {active} active
      </div>
      {active > 0 && (
        <div data-testid={`premium-impressions-${row.supermarket_id}`}
          style={{ fontSize: '0.7rem', color: '#059669', marginTop: 2, fontWeight: 600 }}>
          👁 {totalImpr.toLocaleString()} views · {uniqueImpr.toLocaleString()} unique (7d)
        </div>
      )}
      {active > 0 && (impressions?.clicks || 0) > 0 && (
        <div data-testid={`premium-clicks-${row.supermarket_id}`}
          style={{ fontSize: '0.7rem', color: '#DB2777', marginTop: 2, fontWeight: 600 }}>
          👆 {impressions.clicks.toLocaleString()} clicks · CTR {impressions.ctr_percent}%
        </div>
      )}
      {requested > 0 && (
        <div style={{ fontSize: '0.7rem', color: '#B45309', marginTop: 2, fontWeight: 700 }}>
          {requested} upgrade{requested === 1 ? '' : 's'} requested
        </div>
      )}
      {!editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, color: 'var(--text-muted)' }}>
          <span>@ K{nfmt.format(row.premium_slot_price ?? 100)}/slot</span>
          <button onClick={() => setEditing(true)} data-testid={`premium-price-edit-${row.supermarket_id}`}
            style={{ background: 'transparent', border: 'none', color: 'var(--navy)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.7rem', padding: 0 }}>
            edit
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
          K<input type="number" value={price} onChange={e => setPrice(e.target.value)}
            data-testid={`premium-price-input-${row.supermarket_id}`}
            style={{ width: 56, padding: '3px 6px', border: '1px solid var(--border-gray)', borderRadius: 4, fontSize: '0.75rem' }} min="0" />
          <button onClick={save} disabled={saving} data-testid={`premium-price-save-${row.supermarket_id}`}
            style={{ padding: '3px 8px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? '…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setPrice(row.premium_slot_price ?? 100); }}
            style={{ padding: '3px 6px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.7rem' }}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
